import React, { useCallback } from 'react'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { PlayButtonThumbnailOverlay } from '@/atoms/PlayButtonThumbnailOverlay'
import { CaptionSM } from '@/atoms/Text'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import { ProgressOverlay } from '@/molecules/MediaCard/ProgressOverlay'
import {
  BrandLandingTabItemInteraction,
  useIsolatedCardInteractions,
  useImpressionTracking,
} from '@/services/Analytics/ContentInteractions'
import { VideoMetaData } from '@/services/ProjectsService'
import { useTranslate } from '@/utils/translate/translate-client'

export interface BonusVideoCardProps {
  video: VideoMetaData
  projectSlug: string
  itemIndex: number
  pagePlacement: number
  durationLabel?: string
  onVideoClick?: (video: VideoMetaData) => void
}

export const BonusVideoCard: React.FC<BonusVideoCardProps> = ({
  video,
  projectSlug,
  itemIndex,
  pagePlacement,
  durationLabel,
  onVideoClick,
}) => {
  const { t } = useTranslate('watch')
  const [cardRef, isVisible] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.9,
  })

  const videoId = video.guid
  const videoThumbnailPath = video.posterLandscapeCloudinaryPath
  const href = `/watch/${projectSlug}/videos/${video.page}/${video.guid}`

  let progress = 0
  if (video?.watchPosition?.position && video.source?.duration) {
    progress = (video?.watchPosition?.position / video?.source.duration) * 100
  }

  const createInteraction = useCallback(
    (item: VideoMetaData) => {
      const id = item.guid
      const thumbnailPath = item.posterLandscapeCloudinaryPath
      if (!id || !thumbnailPath) return null

      return new BrandLandingTabItemInteraction({
        id,
        __typename: 'ContentLivestream',
        attribution: {
          itemIndex,
          pagePlacement,
          image: {
            cloudinaryPath: thumbnailPath,
          },
        },
      })
        .withSurface('brand_landing')
        .withPlacement('extras_tab', pagePlacement, itemIndex, 'vertical_list')
        .withImageArtwork(thumbnailPath, 'landscape_display')
    },
    [itemIndex, pagePlacement],
  )

  const { handleMouseEnter, handleMouseLeave, handleClick } = useIsolatedCardInteractions({
    contentId: videoId,
    item: video,
    itemIndex,
    createInteraction,
    onClickTrack: () => onVideoClick?.(video),
  })

  useImpressionTracking({
    isVisible,
    contentId: videoId,
    item: video,
    itemIndex,
    createInteraction,
  })

  return (
    <div
      ref={cardRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="flex w-full flex-row items-start gap-4"
    >
      <div className="shrink-0 snap-center snap-always">
        <InternalLink href={href} className="relative cursor-pointer" onClick={handleClick}>
          <Image
            alt={`${video.title} Bonus Content Thumbnail`}
            src={videoThumbnailPath || ''}
            className="h-full max-h-[82px] w-full max-w-[146px] rounded-lg lg:max-h-[177px] lg:max-w-[316px]"
            width={316}
            height={177}
          />
          <PlayButtonThumbnailOverlay />
          <ProgressOverlay progress={progress} />
        </InternalLink>
      </div>
      <div className="space-between flex h-full w-full flex-row items-center gap-x-4 lg:items-start lg:gap-x-0">
        <div className="flex grow-1 flex-col gap-[6px]">
          <CaptionSM color="core-gray-500" className="md:photon-caption-md lg:photon-caption-lg">
            {t('bonus', 'Bonus')}
          </CaptionSM>
          <CaptionSM weight="bold" className="lg:photon-title-md line-clamp-2 lg:line-clamp-4">
            {video.subtitle}
          </CaptionSM>
          {durationLabel && (
            <CaptionSM color="core-gray-500" className="md:photon-paragraph-md">
              {durationLabel}
            </CaptionSM>
          )}
        </div>
        <a className="lg:hidden" href={href}>
          <CaretRightIcon size={24} color="white" />
        </a>
      </div>
    </div>
  )
}
